import {
  type AttendanceOnDemandIntegrationConfig,
  AttendanceVerificationMethod,
  type AttendanceVerificationMethodConfig,
  type MeshIntegrationConfig,
  type UkgIntegrationConfig,
  type UkgReadyIntegrationConfig,
} from "@clipboard-health/contract-backend-main";
import { Box, FormControl, FormControlLabel, Radio, RadioGroup, Stack } from "@mui/material";
import { type WorkplaceExternalIntegration } from "@src/appV2/ExternalIntegrations/api/useGetWorkplaceExternalIntegrations";
import { Text } from "@src/appV2/redesign/components/Text";
import { useCallback } from "react";

import {
  pinsToEnterStringSchema,
  timeclockLocationStringSchema,
  TUTORIAL_SECTION,
  type TutorialStep,
} from "../types";
import { SaveableTextField } from "./SaveableTextField";
import { TutorialStepsSection } from "./TutorialStepsSection";
import { UkgBusinessUnitNameField } from "./UkgBusinessUnitNameField";

const METHODS_WITH_TUTORIAL_AND_CREDENTIALS = new Set([
  AttendanceVerificationMethod.UKG_INTEGRATION,
  AttendanceVerificationMethod.ATTENDANCE_ON_DEMAND_INTEGRATION,
]);

type ExternalIntegrationMethod =
  | UkgIntegrationConfig
  | UkgReadyIntegrationConfig
  | AttendanceOnDemandIntegrationConfig
  | MeshIntegrationConfig;

interface ExternalIntegrationConfigProps {
  method: ExternalIntegrationMethod;
  onSave: (config: AttendanceVerificationMethodConfig) => void;
  workplaceId: string;
  isCbhAdmin: boolean;
  externalIntegration?: WorkplaceExternalIntegration;
  parentExternalIntegration?: WorkplaceExternalIntegration;
}

const VENDOR_OPTIONS = [
  { value: AttendanceVerificationMethod.UKG_INTEGRATION, label: "UKG" },
  {
    value: AttendanceVerificationMethod.ATTENDANCE_ON_DEMAND_INTEGRATION,
    label: "Attendance on Demand",
  },
  { value: AttendanceVerificationMethod.MESH_INTEGRATION, label: "Mesh" },
] as const;

export function ExternalIntegrationConfig(props: ExternalIntegrationConfigProps) {
  const {
    method,
    onSave,
    workplaceId,
    isCbhAdmin,
    externalIntegration,
    parentExternalIntegration,
  } = props;

  const handleVendorChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newType = event.target.value as (typeof VENDOR_OPTIONS)[number]["value"];

      const existingTutorial = method.tutorial ?? { stepsClockIn: [], stepsClockOut: [] };
      const existingTimeclockLocation = method.timeclockLocationString ?? "";
      const existingPinsToEnterString = method.pinsToEnterString ?? "";
      const existingKioskLocation =
        method.type === AttendanceVerificationMethod.MESH_INTEGRATION ? method.kioskLocation : "";

      const newConfig: AttendanceVerificationMethodConfig =
        newType === AttendanceVerificationMethod.MESH_INTEGRATION
          ? {
              type: newType,
              isEnabled: method.isEnabled,
              timeclockLocationString: existingTimeclockLocation,
              pinsToEnterString: existingPinsToEnterString,
              tutorial: existingTutorial,
              kioskLocation: existingKioskLocation,
            }
          : {
              type: newType,
              isEnabled: method.isEnabled,
              timeclockLocationString: existingTimeclockLocation,
              pinsToEnterString: existingPinsToEnterString,
              tutorial: existingTutorial,
            };

      onSave(newConfig);
    },
    [method, onSave]
  );

  const handleLocationSave = useCallback(
    async (value: string) => {
      onSave({
        ...method,
        timeclockLocationString: value,
      });
    },
    [method, onSave]
  );

  const handlePinsSave = useCallback(
    async (value: string) => {
      onSave({
        ...method,
        pinsToEnterString: value,
      });
    },
    [method, onSave]
  );

  const handleClockInStepsSave = useCallback(
    async (steps: TutorialStep[]) => {
      onSave({
        ...method,
        tutorial: {
          ...method.tutorial,
          stepsClockIn: steps,
        },
      });
    },
    [method, onSave]
  );

  const handleClockOutStepsSave = useCallback(
    async (steps: TutorialStep[]) => {
      onSave({
        ...method,
        tutorial: {
          ...method.tutorial,
          stepsClockOut: steps,
        },
      });
    },
    [method, onSave]
  );

  const handleRadioChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleVendorChange(event);
    },
    [handleVendorChange]
  );

  return (
    <Stack spacing={3}>
      <Box>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Text variant="body1" sx={{ minWidth: 60 }}>
            Vendor
          </Text>
          <FormControl>
            <RadioGroup row value={method.type} onChange={handleRadioChange}>
              {VENDOR_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio size="small" />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Stack>
      </Box>

      {isCbhAdmin && method.type === AttendanceVerificationMethod.UKG_INTEGRATION && (
        <UkgBusinessUnitNameField
          workplaceId={workplaceId}
          externalIntegration={externalIntegration}
          parentExternalIntegration={parentExternalIntegration}
        />
      )}

      <Box>
        <SaveableTextField
          label="Time clock location"
          value={method.timeclockLocationString ?? ""}
          placeholder="In the room with the copier"
          schema={timeclockLocationStringSchema}
          onSave={handleLocationSave}
        />
      </Box>
      {METHODS_WITH_TUTORIAL_AND_CREDENTIALS.has(method.type) && (
        <>
          <Box>
            <SaveableTextField
              label="Credentials template"
              value={method.pinsToEnterString ?? ""}
              placeholder="1234, 5678"
              schema={pinsToEnterStringSchema}
              onSave={handlePinsSave}
            />
          </Box>
          <TutorialStepsSection
            required
            title="Steps for clocking in"
            section={TUTORIAL_SECTION.TIMECLOCK_USE}
            steps={method.tutorial.stepsClockIn}
            onSaveStep={handleClockInStepsSave}
          />

          <TutorialStepsSection
            required
            title="Steps for clocking out"
            section={TUTORIAL_SECTION.TIMECLOCK_USE}
            steps={method.tutorial.stepsClockOut}
            onSaveStep={handleClockOutStepsSave}
          />
        </>
      )}
    </Stack>
  );
}
